import { HintAnimation } from '../hint-motion';
import { hline, pill, pressed, text } from '../../academy/films/story-scene';
import { abs, C, glass, icon, orbiTemplate } from './scene-kit';
import { CONTENT, glowLayer, HintStory, inputField, outlinePill, portalShell } from './hint-story';

/**
 * Hinweis C „Erstelle deinen ersten KI-Agenten“.
 *
 * Didaktik: Der allererste Schritt — die leere Seite darf nicht abschrecken.
 * WARUM „Dein eigener KI-Agent — in wenigen Klicks“ → WO KI-Agenten ›
 * „Hinzufügen“ → WAS Vorlage wählen, „Weiter“, Namen geben (wie im echten
 * Anlege-Dialog: Orbi freut sich auf seine Aufgabe), „Erstellen“ →
 * GESCHAFFT die leere Kachel wird zum Agenten, Orbi lacht. Drei Klicks und
 * ein Name — mehr braucht der Einstieg nicht.
 */

const DLG = { x: 150, y: 72, w: 340, h: 236 };
const ADD: [number, number] = [CONTENT.x + 395, CONTENT.y + 25];
const TPL: [number, number] = [DLG.x + 18 + 48, DLG.y + 60 + 48];
const PRIMARY: [number, number] = [DLG.x + 271, DLG.y + 202];

const templates: [string, string][] = [
  ['template', 'Alpha Tutor'],
  ['users', 'Alpha Twin'],
  ['chat', 'Alpha Support'],
];

const content =
  text(20, 12, 300, 'KI-Agenten', 18, 400) +
  outlinePill(346, 10, 98, 'Hinzufügen', 'add') +
  glowLayer('addGlow', 346, 10, 98, 30) +
  hline(16, 48, 428) +
  `<div data-r="empty" style="${abs(20, 64, 130, 110, `border-radius:18px;border:1.5px dashed rgb(83 109 145 / .35);display:flex;align-items:center;justify-content:center;font-size:11.5px;font-weight:500;color:${C.muted};`)}">Noch kein Agent</div>` +
  `<div data-r="tile" style="opacity:0;transform-origin:85px 55px;">` +
  glass(
    18,
    0.55,
    'left:20px;top:64px;width:130px;height:110px;',
    `<div style="${abs(47, 12, 36, 36)}">${orbiTemplate('tile', 36)}</div>` +
      text(6, 56, 118, 'Statistik-Tutor', 12.5, 600, C.text, 'text-align:center;') +
      `<div style="${abs(25, 80, 80, 20, `border-radius:999px;background:rgb(83 109 145 / .12);display:flex;align-items:center;justify-content:center;font-size:10.5px;font-weight:600;color:${C.muted};`)}">Alpha Tutor</div>`,
  ) +
  `</div>`;

const step1 =
  `<div data-r="step1">` +
  text(18, 14, 300, 'Wähle eine Vorlage', 14, 600) +
  hline(14, 44, 312) +
  templates
    .map(
      ([ic, name], i) =>
        `<div style="${abs(18 + i * 106, 60, 96, 96)}">` +
        (i === 0 ? pressed(0, 0, 96, 96, 'tplSel').replace('style="', 'style="opacity:0;') : '') +
        `<div style="${abs(0, 0, 96, 96, 'border-radius:14px;box-shadow:inset 0 0 0 1px rgb(83 109 145 / .16);')}"></div>` +
        `<div style="${abs(36, 20, 24, 24)}">${icon(ic, 24, C.action)}</div>` +
        text(4, 56, 88, name, 11.5, 600, C.text, 'text-align:center;') +
        `</div>`,
    )
    .join('') +
  pill(240, 186, 82, 'Weiter') +
  `</div>`;

const step2 =
  `<div data-r="step2" style="opacity:0;">` +
  `<div style="${abs(146, 14, 48, 48)}">${orbiTemplate('dlg', 48)}</div>` +
  text(0, 68, 340, 'Gute Wahl!', 14, 600, C.text, 'text-align:center;') +
  text(40, 100, 260, 'Gib mir einen Namen', 11.5, 600, C.muted) +
  inputField(40, 120, 260, 'z.B. Mathe-Tutor', 'ph', 'typed', 34) +
  pill(220, 186, 102, 'Erstellen') +
  `</div>`;

const dialog =
  `<div data-r="dlg" style="opacity:0;transform-origin:${DLG.x + DLG.w / 2}px ${DLG.y + DLG.h / 2}px;">` +
  glass(22, 0.94, `left:${DLG.x}px;top:${DLG.y}px;width:${DLG.w}px;height:${DLG.h}px;`, step1 + step2) +
  `</div>`;

export const createAgentAnimation = (): HintAnimation =>
  new HintStory({
    intro: { line1: ['Dein', 'eigener', 'KI-Agent'], line2: ['in', 'wenigen', 'Klicks'] },
    portal: portalShell({ active: 0, content }) + dialog,
    tracks: [
      { r: 'dlg', at: [1.55, 1.75, 3.2, 3.32], o: [0, 1, 1, 0], s: [0.96, 1, 1, 0.98] },
      { r: 'tplSel', at: [1.95, 2.02], o: [0, 1] },
      { r: 'step1', at: [2.3, 2.38], o: [1, 0] },
      { r: 'step2', at: [2.36, 2.5], o: [0, 1] },
      { r: 'ph', at: [2.5, 2.53], o: [1, 0] },
      { r: 'empty', at: [3.3, 3.4], o: [1, 0] },
      { r: 'tile', at: [3.36, 3.58], o: [0, 1], s: [0.85, 1] },
    ],
    typing: [{ r: 'typed', text: 'Statistik-Tutor', from: 2.52, to: 2.92 }],
    glows: [{ r: 'addGlow', at: 1.12, dur: 0.45 }],
    orbis: [
      { prefix: 'dlg', pop: 2.42, laugh: 2.95 },
      { prefix: 'tile', pop: 3.5, laugh: 3.62 },
    ],
    cursor: {
      at: [0, 1.1, 1.44, 1.56, 1.88, 2.0, 2.18, 2.3, 3.0, 3.12, 3.7, 99],
      x: [300, 300, ADD[0], ADD[0], TPL[0], TPL[0], PRIMARY[0] - 4, PRIMARY[0] - 4, PRIMARY[0], PRIMARY[0], 520, 520],
      y: [250, 250, ADD[1], ADD[1], TPL[1], TPL[1], PRIMARY[1], PRIMARY[1], PRIMARY[1], PRIMARY[1], 320, 320],
      clicks: [1.5, 1.95, 2.25, 3.08],
    },
    camera: {
      at: [0, 1.58, 1.82, 3.2, 3.5, 99],
      x: [320, 320, 320, 320, 300, 300],
      y: [176, 176, 190, 190, 176, 176],
      s: [1, 1, 1.25, 1.25, 1.1, 1.1],
    },
    still: 3.95,
  });
